import { Box, Button, TextField } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useState } from "react"; // Add useState for loading/error states
import { API_BASE_URL } from "../../config/api";
import { assertAdminPortalAccess, extractLoginRole } from "../../utils/authRole";

// Add Yup validation schema
const loginSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Required"),
  password: yup.string().required("Required"),
});

const LoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Track API loading state
  const [error, setError] = useState(""); // Track API errors

  const handleFormSubmit = async (values) => {
    setLoading(true);
    setError("");
    
    try {
      // Replace with your actual API endpoint
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });
      

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      assertAdminPortalAccess(extractLoginRole(data));

      // Save token to localStorage
      localStorage.setItem("authToken", data.items.token);
      localStorage.setItem("userRole", "admin");
      
      // Navigate to dashboard
      navigate("/dashboard",{ state: { "token": data.items.token } });
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="Admin Login" subtitle="Login to your admin account" />

      {/* Display error message */}
      {error && (
        <Box color="error.main" mb={2}>
          {error}
        </Box>
      )}

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={loginSchema} // Add validation schema
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="email" // Change to email type
                label="Email"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                name="email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="password" // Change to password type
                label="Password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                name="password"
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>
            <Box display="flex" justifyContent="space-between" mt="20px">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate("/")}
              >
                Back
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loading} // Disable during API call
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const initialValues = {
  email: "",
  password: "",
};

export default LoginForm;